import { Injectable } from '@angular/core';
import { FFmpeg } from '@ffmpeg/ffmpeg';

/**
 * API audio is M4A/ALAC. Chrome/Firefox cannot decode ALAC in HTML5 audio;
 * Safari (and macOS Safari WebKit) often can. This service detects ALAC and,
 * when needed, transcodes to WAV in the browser via ffmpeg.wasm.
 *
 * Windows Chrome is stricter about `.wasm` MIME and WASM-heap TypedArrays —
 * we load core/wasm via blob: URLs and copy bytes out of the WASM heap.
 */
@Injectable({
  providedIn: 'root',
})
export class BrowserAudioService {
  private ffmpeg: FFmpeg | null = null;
  private loadPromise: Promise<void> | null = null;
  private blobUrls: string[] = [];

  /**
   * Returns an object URL that HTML5 <audio> can play in major browsers.
   * Caller must revoke the URL when done.
   */
  async toPlayableObjectUrl(blob: Blob): Promise<string> {
    const bytes = new Uint8Array(await blob.arrayBuffer());

    if (!this.isAlacM4a(bytes) || this.canPlayAlacNatively()) {
      const typed =
        blob.type && blob.type !== 'application/octet-stream'
          ? blob
          : new Blob([this.copyBytes(bytes)], { type: 'audio/mp4' });
      return URL.createObjectURL(typed);
    }

    const wav = await this.transcodeAlacToWav(bytes);
    return URL.createObjectURL(wav);
  }

  canPlayAlacNatively(): boolean {
    if (typeof Audio === 'undefined') {
      return false;
    }
    const probe = document.createElement('audio');
    return probe.canPlayType('audio/mp4; codecs="alac"') !== '';
  }

  /**
   * Detect Apple Lossless fourcc inside an ISO BMFF / M4A buffer.
   */
  isAlacM4a(bytes: Uint8Array): boolean {
    const limit = Math.min(bytes.length, 65536);
    for (let i = 0; i < limit - 3; i++) {
      // 'alac'
      if (
        bytes[i] === 0x61 &&
        bytes[i + 1] === 0x6c &&
        bytes[i + 2] === 0x61 &&
        bytes[i + 3] === 0x63
      ) {
        return true;
      }
    }
    return false;
  }

  private async ensureFfmpeg(): Promise<FFmpeg> {
    if (this.ffmpeg?.loaded) {
      return this.ffmpeg;
    }

    if (!this.loadPromise) {
      this.loadPromise = (async () => {
        const ffmpeg = new FFmpeg();
        // Worker must stay same-origin (it imports ./const.js etc.).
        // Core + wasm go through blob: URLs so MIME is correct even when
        // the host (IIS / reverse proxy) serves .wasm as octet-stream —
        // a common Windows Chrome failure mode.
        const assetBase = `${window.location.origin}/assets/ffmpeg`;
        const coreURL = await this.toBlobUrl(
          `${assetBase}/ffmpeg-core.js`,
          'text/javascript'
        );
        const wasmURL = await this.toBlobUrl(
          `${assetBase}/ffmpeg-core.wasm`,
          'application/wasm'
        );

        await ffmpeg.load({
          classWorkerURL: `${assetBase}/worker.js`,
          coreURL,
          wasmURL,
        });
        this.ffmpeg = ffmpeg;
      })().catch(error => {
        this.loadPromise = null;
        this.revokeBlobUrls();
        throw error;
      });
    }

    await this.loadPromise;
    return this.ffmpeg!;
  }

  private async toBlobUrl(url: string, mimeType: string): Promise<string> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `Failed to fetch ffmpeg asset ${url} (${response.status})`
      );
    }
    const buffer = await response.arrayBuffer();
    const blobUrl = URL.createObjectURL(
      new Blob([buffer], { type: mimeType })
    );
    this.blobUrls.push(blobUrl);
    return blobUrl;
  }

  private revokeBlobUrls(): void {
    for (const url of this.blobUrls) {
      URL.revokeObjectURL(url);
    }
    this.blobUrls = [];
  }

  /** Detach from SharedArrayBuffer / WASM heap (Chrome Windows). */
  private copyBytes(bytes: Uint8Array): Uint8Array {
    const copy = new Uint8Array(bytes.byteLength);
    copy.set(bytes);
    return copy;
  }

  private async transcodeAlacToWav(bytes: Uint8Array): Promise<Blob> {
    const ffmpeg = await this.ensureFfmpeg();
    const inputName = 'input.m4a';
    const outputName = 'output.wav';
    const input = this.copyBytes(bytes);

    await ffmpeg.writeFile(inputName, input);
    // Decode ALAC → PCM WAV (universally playable).
    const exitCode = await ffmpeg.exec([
      '-i',
      inputName,
      '-vn',
      '-acodec',
      'pcm_s16le',
      outputName,
    ]);

    if (exitCode !== 0) {
      await ffmpeg.deleteFile(inputName).catch(() => undefined);
      await ffmpeg.deleteFile(outputName).catch(() => undefined);
      throw new Error(`ffmpeg ALAC→WAV failed with code ${exitCode}`);
    }

    const data = await ffmpeg.readFile(outputName);
    await ffmpeg.deleteFile(inputName).catch(() => undefined);
    await ffmpeg.deleteFile(outputName).catch(() => undefined);

    if (!(data instanceof Uint8Array)) {
      throw new Error('Unexpected ffmpeg WAV output type');
    }

    // Must copy: WASM heap views can be invalid after FS ops on Win Chrome.
    return new Blob([this.copyBytes(data)], { type: 'audio/wav' });
  }
}
